import { create } from 'zustand';

export interface AuthUser {
  id: string;
  email: string;
  display_name: string | null;
  photo_url: string | null;
  is_admin: boolean;
  locale?: string;
  user_tier?: 'member' | 'early_tester';
}

export type UserTier = 'guest' | 'member' | 'early_tester';

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  isGuest: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (token: string) => void;
  logout: () => void;
  setUser: (user: AuthUser) => void;
  setGuestMode: (value: boolean) => void;
  setLoading: (value: boolean) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  token: localStorage.getItem('auth_token'),
  isGuest: false,
  isLoading: true,
  isAuthenticated: !!localStorage.getItem('auth_token'),
  login: (token: string) => {
    localStorage.setItem('auth_token', token);
    set({ token, isGuest: false, isAuthenticated: true });
  },
  logout: () => {
    localStorage.removeItem('auth_token');
    set({ user: null, token: null, isGuest: false, isAuthenticated: false });
  },
  setUser: (user: AuthUser) => set({ user, isAuthenticated: true }),
  setGuestMode: (value: boolean) => set({ isGuest: value }),
  setLoading: (value: boolean) => set({ isLoading: value }),
}));

/**
 * Derives the effective user tier from the current auth state.
 * - Guest or unauthenticated (and not loading) → 'guest'
 * - Authenticated user → user.user_tier (defaults to 'member' for legacy data)
 */
export function getUserTier(state: {
  isGuest: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
}): UserTier {
  if (state.isGuest || (!state.isAuthenticated && !state.isLoading)) return 'guest';
  return state.user?.user_tier ?? 'member';
}
